// UltraFlex - PT Login Page
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Dumbbell, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function PTLogin() {
  const navigate = useNavigate();
  const { loginPT, isAuthenticated, userType, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in as PT
  useEffect(() => {
    if (!authLoading && isAuthenticated && userType === 'pt') {
      navigate('/pt/hub', { replace: true });
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  const validate = () => {
    const errors = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Enter a valid email address';
    if (!password) errors.password = 'Password is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await loginPT(email, password);
      navigate('/pt/hub', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="pt-login-page">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-red-600/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Dumbbell className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">PT Login</h1>
          <p className="text-zinc-400 text-sm mt-1">Personal Trainer Portal</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-3 mb-4 text-sm flex items-start gap-2" data-testid="pt-login-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="pt-login-email"
                type="email"
                className={`w-full bg-zinc-800 border rounded-lg pl-12 pr-3 py-2.5 text-white text-sm focus:border-red-500 outline-none transition-colors ${fieldErrors.email ? 'border-red-500' : 'border-zinc-700'}`}
                value={email}
                onChange={e => { setEmail(e.target.value); setFieldErrors(f => ({ ...f, email: '' })); }}
                placeholder="you@example.com"
              />
            </div>
            {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="pt-login-password"
                type="password"
                className={`w-full bg-zinc-800 border rounded-lg pl-12 pr-3 py-2.5 text-white text-sm focus:border-red-500 outline-none transition-colors ${fieldErrors.password ? 'border-red-500' : 'border-zinc-700'}`}
                value={password}
                onChange={e => { setPassword(e.target.value); setFieldErrors(f => ({ ...f, password: '' })); }}
                placeholder="Enter password"
              />
            </div>
            {fieldErrors.password && <p className="text-red-400 text-xs mt-1">{fieldErrors.password}</p>}
          </div>
          <button data-testid="pt-login-submit" type="submit" disabled={loading} className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold transition-colors disabled:opacity-50">{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <p className="text-zinc-500 text-sm">Not registered? <Link to="/pt/register" className="text-red-400 hover:text-red-300">Apply as PT</Link></p>
          <p className="text-zinc-600 text-xs"><Link to="/login" className="hover:text-zinc-400">Member Login</Link> &middot; <Link to="/admin/login" className="hover:text-zinc-400">Staff Login</Link></p>
        </div>
      </motion.div>
    </div>
  );
}
